
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Database } from '@sylva/shared-types';
import { checkDuplicateCustomer, deduplicateAndFilterImportData } from '@/lib/customerUtils';
import { fetchCustomersAndAppointments, fetchCustomerCount, searchCustomersInDatabase } from '@/lib/queries/customers';
import { levenshteinDistance } from '@/lib/stringUtils';
import { normalizePhoneToE164, formatPhoneForDisplay } from '@sylva/shared-types';

// Database types
type DbCustomer = Database['public']['Tables']['customers']['Row'];
type DbCustomerInsert = Database['public']['Tables']['customers']['Insert'];
type DbCustomerUpdate = Database['public']['Tables']['customers']['Update'];

// UI-friendly Customer interface (camelCase for better DX)
export interface Customer {
  id: string;
  tenant_id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  marketing_opt_ins: {
    email: boolean;
    whatsapp: boolean;
  };
  notes: string;
  created_at: string;
  updated_at: string;
  acquisitionSource?: string | null;
  selfReportedFirstVisit?: boolean | null;
  birthdate?: string | null;
  street?: string | null;
  city?: string | null;
  zipCode?: string | null;
  gender?: 'm' | 'f' | 'd' | null;
  reviewRequestedAt?: string | null;
  /** Service the lead expressed interest in (from CTA / portal pre-selection). */
  interestedService?: string | null;
  // Fields from customer_status view
  status?: 'customer' | 'lead' | 'imported' | null;
  created_last_7d?: boolean | null;
  created_last_14d_7d?: boolean | null;
  appointment_count?: number | null;
  /** @deprecated Use status === 'lead' */
  is_lead?: boolean | null;
}

export type SortOption = 'firstName' | 'lastName' | 'email' | 'phone' | 'created_at' | 'birthdate';
export type SortDirection = 'asc' | 'desc';
export type FilterOption = 'all' | 'leads' | 'customers' | 'upcomingBirthdays';

// Helper function to transform database row to Customer interface
// Accepts both direct customers table rows and customer_status view rows
export const transformCustomerRow = (customer: (DbCustomer | (Partial<DbCustomer> & Pick<DbCustomer, 'id' | 'tenant_id' | 'first_name'>)) & { status?: 'customer' | 'lead' | 'imported' | null; created_last_7d?: boolean | null; created_last_14d_7d?: boolean | null; appointment_count?: number | null; interested_service?: string | null }): Customer => ({
  id: customer.id,
  tenant_id: customer.tenant_id,
  firstName: customer.first_name,
  lastName: customer.last_name ?? null,
  email: customer.email ?? null,
  phone: customer.phone ?? null,
  marketing_opt_ins: (customer.marketing_opt_ins as Customer['marketing_opt_ins']) || {
    email: false,
    whatsapp: false,
  },
  notes: customer.notes || '',
  created_at: customer.created_at ?? '',
  updated_at: customer.updated_at ?? '',
  acquisitionSource: customer.acquisition_source ?? null,
  selfReportedFirstVisit: customer.self_reported_first_visit ?? null,
  birthdate: customer.birthdate ?? null,
  street: customer.street ?? null,
  city: customer.city ?? null,
  zipCode: customer.zip_code ?? null,
  gender: (customer.gender as 'm' | 'f' | 'd' | null) ?? null,
  reviewRequestedAt: (customer as { review_requested_at?: string | null }).review_requested_at ?? null,
  interestedService: customer.interested_service ?? null,
  // Fields from customer_status view
  status: customer.status,
  created_last_7d: customer.created_last_7d,
  created_last_14d_7d: customer.created_last_14d_7d,
  appointment_count: customer.appointment_count,
  // Backward compatibility: compute is_lead from status
  is_lead: customer.status === 'lead',
});

// Helper function to detect if a query looks like a phone number
const looksLikePhoneNumber = (query: string): boolean => {
  const cleaned = query.replace(/\s/g, '');
  // Check if it starts with 0, +, or consists mostly of digits
  return /^[0+]/.test(cleaned) || /^\d{6,}$/.test(cleaned);
};

/**
 * @param options.includeList Ob der komplette Kundenstamm geladen werden soll.
 *   Standard ist true, damit bestehende Aufrufer unveraendert funktionieren.
 *
 *   Der TenantProvider setzt das auf false: er braucht aus diesem Hook nur die
 *   Schreiboperationen, hat aber bisher die Liste mitgeladen -- und weil der
 *   Provider um die ganze App liegt, passierte das auf *jeder* Seite. Bei M&M
 *   Hairdesign sind das acht aufeinanderfolgende Anfragen (der Stamm wird in
 *   1000er-Bloecken geholt), auch auf Kalender, Einstellungen oder Aufgaben,
 *   wo ihn niemand anschaut.
 */
export const useCustomers = (
  tenantId: string | null,
  options?: { includeList?: boolean }
) => {
  const includeList = options?.includeList ?? true;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  // The list renders one page at a time (client-side pager), so we can load the
  // whole customer book and still stay snappy. Bounded generously; the rare
  // salon beyond this falls back to database search (see shouldUseDbSearch).
  const CUSTOMER_LIST_FETCH_LIMIT = 25000;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return { customers: [], appointments: [] };
      return fetchCustomersAndAppointments(tenantId, CUSTOMER_LIST_FETCH_LIMIT);
    },
    enabled: includeList && !!tenantId && !!user,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  // Separate query for total customer count
  const { data: totalCustomerCount = 0 } = useQuery({
    queryKey: ['customerCount', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return 0;
      return fetchCustomerCount(tenantId);
    },
    enabled: includeList && !!tenantId && !!user,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  // Separate query for database search when we have too many customers
  const shouldUseDbSearch = totalCustomerCount > CUSTOMER_LIST_FETCH_LIMIT && searchQuery.trim().length > 0;
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['customerSearch', tenantId, searchQuery],
    queryFn: async () => {
      if (!tenantId || !user || !searchQuery.trim()) return [];
      return searchCustomersInDatabase(tenantId, searchQuery, 200);
    },
    enabled: !!tenantId && !!user && shouldUseDbSearch,
    staleTime: 5 * 60 * 1000, // 5 minutes - search results are more volatile
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const allCustomers: Customer[] = (data?.customers || []).map(transformCustomerRow);

  const createCustomer = async (customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      // Check for duplicate customer based on phone + first name
      if (customer.phone && customer.firstName) {
        const duplicate = await checkDuplicateCustomer(
          tenantId,
          customer.email,
          customer.phone,
          undefined,
          true, // TODO: Pass tenant language from TenantContext when available
          customer.firstName
        );

        if (duplicate) {
          return {
            data: null,
            error: {
              message: 'duplicateCustomer',
              isDuplicate: true,
              existingCustomer: {
                firstName: duplicate.first_name,
                lastName: duplicate.last_name
              }
            }
          };
        }
      }

      const insertData: DbCustomerInsert = {
        tenant_id: tenantId,
        first_name: customer.firstName,
        last_name: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        marketing_opt_ins: customer.marketing_opt_ins,
        notes: customer.notes,
        birthdate: customer.birthdate,
        street: customer.street,
        city: customer.city,
        zip_code: customer.zipCode,
        gender: customer.gender,
      };

      const { data, error } = await supabase
        .from('customers')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      const transformedCustomer = transformCustomerRow(data);

      // Update cached list optimistically
      queryClient.setQueryData(['customers', tenantId], (oldData: any) => {
        const existing = oldData?.customers || [];
        return {
          customers: [
            {
              id: data.id,
              tenant_id: data.tenant_id,
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
              marketing_opt_ins: data.marketing_opt_ins,
              notes: data.notes,
              created_at: data.created_at,
              updated_at: data.updated_at,
              email_verification_sent_at: null,
              email_verification_token: null,
              email_verified: null,
              email_verified_at: null,
              birthdate: data.birthdate,
            },
            ...existing,
          ],
          appointments: oldData?.appointments || [],
        };
      });

      // Update customer count cache optimistically
      queryClient.setQueryData(['customerCount', tenantId], (oldCount: number = 0) => {
        return oldCount + 1;
      });

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['customerCount', tenantId] });

      return { data: transformedCustomer, error: null as any };
    } catch (error) {
      console.error('Error creating customer:', error);
      return { data: null, error };
    }
  };

  const bulkCreateCustomers = async (
    customers: Omit<Customer, 'id' | 'created_at' | 'updated_at'>[],
    onProgress?: (current: number, total: number, customerName: string) => void
  ) => {
    if (!user || !tenantId) return { successful: 0, duplicates: 0, failed: 0 };

    const isGermanTenant = true; // TODO: Get from tenant context when available
    const originalCount = customers.length;

    // Step 1: Deduplicate and filter against existing customers (SINGLE BATCH OPERATION)
    const { newCustomers, duplicates, withinImportDuplicates } = await deduplicateAndFilterImportData(
      customers,
      isGermanTenant
    );

    const results = {
      successful: 0,
      duplicates: duplicates.length + withinImportDuplicates.length,
      failed: 0
    };

    console.log(`Import deduplication: ${originalCount} -> ${newCustomers.length} (${results.duplicates} duplicates)`);

    if (newCustomers.length === 0) {
      return results;
    }

    // Step 2: Bulk insert new customers without individual duplicate checks
    const BATCH_SIZE = 100; // Process in smaller batches to avoid overwhelming the database

    for (let i = 0; i < newCustomers.length; i += BATCH_SIZE) {
      const batch = newCustomers.slice(i, i + BATCH_SIZE);

      const batchPromises = batch.map(async (customer, batchIndex) => {
        const currentIndex = i + batchIndex + 1;
        const customerName = `${customer.firstName} ${customer.lastName}`;

        // Update progress
        onProgress?.(currentIndex, originalCount, customerName);

        try {
          // Convert customer data for database (no duplicate check needed - already done)
          const dbCustomer: DbCustomerInsert = {
            tenant_id: customer.tenant_id,
            first_name: customer.firstName,
            last_name: customer.lastName,
            email: customer.email || null,
            phone: customer.phone || null,
            birthdate: customer.birthdate,
            street: customer.street,
            city: customer.city,
            zip_code: customer.zipCode,
            marketing_opt_ins: customer.marketing_opt_ins,
            notes: customer.notes || '',
            gender: customer.gender,
            acquisition_source: 'bulk_import' // Mark as bulk import instead of default 'platform'
          };

          const { data, error } = await supabase
            .from('customers')
            .insert(dbCustomer)
            .select()
            .single();

          if (error) throw error;

          results.successful++;
          return { status: 'success', data };
        } catch (error) {
          console.error(`Error creating customer ${customerName}:`, error);
          results.failed++;
          return { status: 'failed', error };
        }
      });

      // Wait for this batch to complete before moving to the next
      await Promise.allSettled(batchPromises);
    }

    // Refresh the customer list and count only once at the end
    queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['customerCount', tenantId] });

    return results;
  };

  const updateCustomer = async (id: string, updates: Partial<Customer>) => {
    try {
      // Check for duplicate based on phone + first name if both are being updated
      // Note: We need the firstName to perform deduplication check
      if (updates.phone && updates.firstName && tenantId) {
        const duplicate = await checkDuplicateCustomer(
          tenantId,
          updates.email ?? null,
          updates.phone ?? null,
          id, // Exclude current customer from check
          true, // TODO: Pass tenant language from TenantContext when available
          updates.firstName
        );

        if (duplicate) {
          return {
            data: null,
            error: {
              message: 'duplicateCustomer',
              isDuplicate: true,
              existingCustomer: {
                firstName: duplicate.first_name,
                lastName: duplicate.last_name
              }
            }
          };
        }
      } else if (updates.phone && !updates.firstName && tenantId) {
        // If only phone is being updated, we need to fetch the current firstName
        const { data: currentCustomer } = await supabase
          .from('customers')
          .select('first_name')
          .eq('id', id)
          .single();

        if (currentCustomer?.first_name) {
          const duplicate = await checkDuplicateCustomer(
            tenantId,
            updates.email ?? null,
            updates.phone ?? null,
            id,
            true,
            currentCustomer.first_name
          );

          if (duplicate) {
            return {
              data: null,
              error: {
                message: 'duplicateCustomer',
                isDuplicate: true,
                existingCustomer: {
                  firstName: duplicate.first_name,
                  lastName: duplicate.last_name
                }
              }
            };
          }
        }
      }

      const updateData: DbCustomerUpdate = {};
      if (updates.firstName !== undefined) updateData.first_name = updates.firstName;
      if (updates.lastName !== undefined) updateData.last_name = updates.lastName;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.marketing_opt_ins !== undefined) updateData.marketing_opt_ins = updates.marketing_opt_ins;
      if (updates.birthdate !== undefined) updateData.birthdate = updates.birthdate;
      if (updates.street !== undefined) updateData.street = updates.street;
      if (updates.city !== undefined) updateData.city = updates.city;
      if (updates.zipCode !== undefined) updateData.zip_code = updates.zipCode;
      if (updates.gender !== undefined) updateData.gender = updates.gender;

      const { data, error } = await supabase
        .from('customers')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const transformedCustomer = transformCustomerRow(data);

      // Update cached list
      queryClient.setQueryData(['customers', tenantId], (oldData: any) => {
        const existing = oldData?.customers || [];
        return {
          customers: existing.map((c: any) =>
            c.id === id
              ? {
                ...c,
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                phone: data.phone,
                marketing_opt_ins: data.marketing_opt_ins,
                notes: data.notes,
                updated_at: data.updated_at,
                birthdate: data.birthdate,
              }
              : c
          ),
          appointments: oldData?.appointments || [],
        };
      });

      // Invalidate conversations cache so it refetches with updated customer data
      queryClient.invalidateQueries({ queryKey: ['conversations', tenantId] });

      return { data: transformedCustomer, error: null as any };
    } catch (error) {
      console.error('Error updating customer:', error);
      return { data: null, error };
    }
  };

  const deleteCustomer = async (id: string) => {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update cache optimistically
      queryClient.setQueryData(['customers', tenantId], (oldData: any) => {
        const existing = oldData?.customers || [];
        const existingAppointments = oldData?.appointments || [];
        return {
          customers: existing.filter((c: any) => c.id !== id),
          appointments: existingAppointments.filter((a: any) => a.customer_id !== id),
        };
      });

      // Update customer count cache optimistically
      queryClient.setQueryData(['customerCount', tenantId], (oldCount: number = 0) => {
        return Math.max(0, oldCount - 1);
      });

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['customerCount', tenantId] });

      return { error: null };
    } catch (error) {
      console.error('Error deleting customer:', error);
      return { error };
    }
  };

  const sendReviewRequest = async (customerId: string) => {
    if (!user || !tenantId) return { error: 'Not authenticated or no tenant selected' };

    try {
      const { error } = await supabase.rpc("enqueue_notification_job", {
        p_tenant_id: tenantId,
        p_notification_type: "review_request",
        p_payload: {
          customer_id: customerId,
        }
      });

      if (error) throw error;

      // Update the customer's review_requested_at timestamp
      const { error: updateError } = await supabase
        .from('customers')
        .update({ review_requested_at: new Date().toISOString() })
        .eq('id', customerId)
        .eq('tenant_id', tenantId);

      if (updateError) {
        console.error('Error updating review_requested_at:', updateError);
        // Don't fail the request, the email was still queued
      }

      // Invalidate customers query to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });

      return { error: null };
    } catch (error) {
      console.error('Error sending review request:', error);
      return { error };
    }
  };

  const getLastReviewRequestDate = async (customerId: string): Promise<Date | null> => {
    if (!tenantId) return null;

    try {
      const { data, error } = await supabase
        .from('notification_queue')
        .select('sent_at, created_at')
        .eq('tenant_id', tenantId)
        .eq('notification_type', 'review_request')
        .eq('payload->>customer_id', customerId)
        .in('status', ['sent', 'pending', 'processing'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) return null;

      return new Date(data.sent_at || data.created_at);
    } catch {
      return null;
    }
  };

  const bulkDeleteCustomers = async (customerIds: string[], onProgress?: (current: number, total: number) => void) => {
    try {
      // Process in batches to avoid URL length limits
      const BATCH_SIZE = 50; // Safe batch size to stay under URL limits
      const batches = [];

      for (let i = 0; i < customerIds.length; i += BATCH_SIZE) {
        batches.push(customerIds.slice(i, i + BATCH_SIZE));
      }

      let deletedCount = 0;

      // Delete each batch sequentially
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const { error } = await supabase
          .from('customers')
          .delete()
          .in('id', batch);

        if (error) throw error;

        deletedCount += batch.length;
        onProgress?.(deletedCount, customerIds.length);
      }

      // Update cache optimistically
      queryClient.setQueryData(['customers', tenantId], (oldData: any) => {
        const existing = oldData?.customers || [];
        const existingAppointments = oldData?.appointments || [];
        return {
          customers: existing.filter((c: any) => !customerIds.includes(c.id)),
          appointments: existingAppointments.filter((a: any) => !customerIds.includes(a.customer_id)),
        };
      });

      // Update customer count cache optimistically
      queryClient.setQueryData(['customerCount', tenantId], (oldCount: number = 0) => {
        return Math.max(0, oldCount - customerIds.length);
      });

      // Invalidate queries to trigger fresh fetch and ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['customerCount', tenantId] });

      setSelectedCustomers([]);
      return { error: null };
    } catch (error) {
      console.error('Error bulk deleting customers:', error);
      return { error };
    }
  };

  const filteredAndSortedCustomers = useMemo(() => {
    // Transform search results when using database search
    // Note: searchResults come from customer_status view which has nullable timestamps
    const searchedCustomers: Customer[] = shouldUseDbSearch
      ? (searchResults || []).map((result) => transformCustomerRow(result as any))
      : [];

    // Use database search results if we have too many customers and a search query
    let filtered = shouldUseDbSearch ? searchedCustomers : allCustomers;

    // Apply filter by type (all, leads, customers, upcomingBirthdays)
    if (filterBy === 'leads') {
      // Filter for status = 'lead'
      filtered = allCustomers.filter(customer => customer.status === 'lead');
    } else if (filterBy === 'customers') {
      // Filter for status = 'customer'
      filtered = allCustomers.filter(customer => customer.status === 'customer');
    } else if (filterBy === 'upcomingBirthdays') {
      // Filter for birthdays in the next 7 days
      const today = new Date();
      const currentYear = today.getFullYear();

      filtered = allCustomers.filter(customer => {
        if (!customer.birthdate) return false;

        const birthdate = new Date(customer.birthdate);
        const birthMonth = birthdate.getMonth() + 1;
        const birthDay = birthdate.getDate();

        // Create dates for comparison (using current year)
        const thisBirthday = new Date(currentYear, birthMonth - 1, birthDay);
        const nextBirthday = new Date(currentYear + 1, birthMonth - 1, birthDay);

        // Check if birthday already passed this year
        const birthdayToCheck = thisBirthday >= today ? thisBirthday : nextBirthday;

        // Calculate days until birthday
        const daysUntilBirthday = Math.ceil((birthdayToCheck.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        return daysUntilBirthday >= 0 && daysUntilBirthday <= 7;
      });
    }

    // Apply search filter (only if not using database search)
    if (searchQuery && !shouldUseDbSearch) {
      const query = searchQuery.toLowerCase().trim();
      const words = query.split(/\s+/).filter(word => word.length > 0);

      // Detect if query looks like a phone number and normalize it
      const normalizedPhoneQuery = looksLikePhoneNumber(query)
        ? normalizePhoneToE164(query, 'de')
        : null;

      filtered = filtered.filter(customer => {
        const firstName = customer.firstName.toLowerCase();
        const lastName = (customer.lastName || '').toLowerCase();
        const email = (customer.email || '').toLowerCase();
        const phone = (customer.phone || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;

        // If query looks like a phone number, do phone-specific matching
        if (looksLikePhoneNumber(query)) {
          // Try exact match with normalized E.164 version
          if (normalizedPhoneQuery && customer.phone === normalizedPhoneQuery) {
            return true;
          }

          // For partial phone queries, also check national format
          // Convert stored E.164 phone to national format (e.g., +4922198765432 -> 022198765432)
          if (customer.phone) {
            const nationalPhone = formatPhoneForDisplay(customer.phone, 'national').replace(/\s/g, '').toLowerCase();
            const cleanQuery = query.replace(/\s/g, '');
            if (nationalPhone.includes(cleanQuery)) {
              return true;
            }
            // Also check E.164 format directly
            if (phone.includes(cleanQuery)) {
              return true;
            }
          }
        }

        if (words.length === 1) {
          // Single word: search in all fields
          const word = words[0];
          return firstName.includes(word) ||
                 lastName.includes(word) ||
                 email.includes(word) ||
                 phone.includes(word);
        } else if (words.length === 2) {
          // Two words: check various combinations
          const [word1, word2] = words;
          return (
            // First word in firstName AND second word in lastName
            (firstName.includes(word1) && lastName.includes(word2)) ||
            // Second word in firstName AND first word in lastName (reversed)
            (firstName.includes(word2) && lastName.includes(word1)) ||
            // Both words in firstName
            (firstName.includes(word1) && firstName.includes(word2)) ||
            // Both words in lastName
            (lastName.includes(word1) && lastName.includes(word2)) ||
            // Full name contains the complete query
            fullName.includes(query) ||
            // Either word in email
            email.includes(word1) || email.includes(word2) ||
            // Either word in phone
            phone.includes(word1) || phone.includes(word2)
          );
        } else {
          // More than two words: all words must be found somewhere in name fields
          return words.every(word =>
            firstName.includes(word) ||
            lastName.includes(word) ||
            email.includes(word) ||
            phone.includes(word)
          );
        }
      });
    }

    // Apply sorting - prioritize search relevance if there's a search query
    const sorted = [...filtered].sort((a, b) => {
      // If there's a search query, sort by similarity
      if (searchQuery && !shouldUseDbSearch) {
        const query = searchQuery.toLowerCase().trim();

        // Calculate similarity for both customers
        const words = query.split(/\s+/).filter(word => word.length > 0);

        const calculateCustomerSimilarity = (customer: Customer) => {
          const fullName = `${customer.firstName} ${customer.lastName || ''}`.toLowerCase().trim();
          const firstName = customer.firstName.toLowerCase();
          const lastName = (customer.lastName || '').toLowerCase();

          if (words.length === 2) {
            const [word1, word2] = words;

            // Check for exact first+last match (either order)
            const exactMatch1 = firstName.includes(word1) && lastName.includes(word2);
            const exactMatch2 = firstName.includes(word2) && lastName.includes(word1);

            if (exactMatch1 || exactMatch2) {
              // For exact combinations, use very low distance (highest priority)
              return levenshteinDistance(query, fullName);
            }

            // For non-exact matches, add penalty
            const fullNameDistance = levenshteinDistance(query, fullName) + 10;
            const firstNameDistance = levenshteinDistance(query, firstName) + 15;
            const lastNameDistance = levenshteinDistance(query, lastName) + 15;

            return Math.min(fullNameDistance, firstNameDistance, lastNameDistance);
          } else {
            // For single word or 3+ words, use normal distance
            return Math.min(
              levenshteinDistance(query, fullName),
              levenshteinDistance(query, firstName),
              levenshteinDistance(query, lastName)
            );
          }
        };

        const aDistance = calculateCustomerSimilarity(a);
        const bDistance = calculateCustomerSimilarity(b);

        // Lower distance = better match = should come first
        if (aDistance !== bDistance) {
          return aDistance - bDistance;
        }
      }

      // Default sorting by specified field
      const aValue = a[sortBy] || '';
      const bValue = b[sortBy] || '';

      if (sortDirection === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });

    return sorted;
  }, [allCustomers, searchResults, searchQuery, sortBy, sortDirection, filterBy, shouldUseDbSearch]);

  const toggleCustomerSelection = (customerId: string) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const selectAllCustomers = () => {
    setSelectedCustomers(filteredAndSortedCustomers.map(c => c.id));
  };

  const clearSelection = () => {
    setSelectedCustomers([]);
  };

  // Subscribe to real-time updates using broadcast
  useEffect(() => {
    if (!tenantId) return undefined;

    const customersChannel = supabase
      .channel(`tenant:${tenantId}:customers`, {
        config: { private: true }
      })
      .on(
        'broadcast',
        { event: '*' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
          queryClient.invalidateQueries({ queryKey: ['customerCount', tenantId] });
        }
      );

    // Set auth and subscribe
    const setupChannel = async () => {
      await supabase.realtime.setAuth();
      await customersChannel.subscribe();
    };

    setupChannel();

    return () => {
      supabase.removeChannel(customersChannel);
    };
  }, [tenantId, queryClient]);

  const isAllSelected = selectedCustomers.length === filteredAndSortedCustomers.length && filteredAndSortedCustomers.length > 0;
  const isPartiallySelected = selectedCustomers.length > 0 && selectedCustomers.length < filteredAndSortedCustomers.length;

  return {
    customers: filteredAndSortedCustomers,
    allCustomers: allCustomers,
    totalCustomerCount,
    isLoading: isLoading || (shouldUseDbSearch && isSearching),
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    filterBy,
    setFilterBy,
    selectedCustomers,
    setSelectedCustomers,
    toggleCustomerSelection,
    selectAllCustomers,
    clearSelection,
    isAllSelected,
    isPartiallySelected,
    createCustomer,
    bulkCreateCustomers,
    updateCustomer,
    deleteCustomer,
    bulkDeleteCustomers,
    sendReviewRequest,
    getLastReviewRequestDate,
    refetch,
  };
};
